import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Badge } from './ui/badge';
import { MapPin, Plus, X, Loader2, Save, Crosshair, Trash2 } from 'lucide-react';
import { useToast } from '../hooks/use-toast';

const API = `${window.__BACKEND_URL__}/api`;

/**
 * Work Areas manager (Feb 2026, with GPS auto-suggest support).
 *
 * Each work area is `{name, lat?, lng?, radius_km?}`. Pinning lat/lng
 * lets workers' clock-in flow auto-suggest the closest area to their
 * current GPS — see ClockInAreaPicker. Backend endpoints:
 *   • GET /api/clock-in/areas             returns `[{name, lat, lng, radius_km, distance_km, suggested}]`
 *   • PUT /api/clock-in/areas             overwrites the array (admin only)
 *
 * UX notes
 * --------
 * - Each chip has a "Pin location" link → opens a small inline editor.
 *   The admin can either tap "Use my current GPS" (instant) or paste
 *   coords manually. Default radius is 25km.
 * - Removing coords reverts an area to the legacy unpinned state.
 * - Save button only enables when the local list differs from the server.
 */
const DEFAULT_RADIUS_KM = 25;

export default function WorkAreasManager({ token, isAdmin }) {
  const { toast } = useToast();
  const [areas, setAreas] = useState([]); // [{name, lat, lng, radius_km}]
  const [original, setOriginal] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [draft, setDraft] = useState('');
  const [editingIdx, setEditingIdx] = useState(-1);
  const [editLat, setEditLat] = useState('');
  const [editLng, setEditLng] = useState('');
  const [editRadius, setEditRadius] = useState('');
  const [gpsBusy, setGpsBusy] = useState(false);

  const normalize = (raw) => {
    // Tolerate the server returning enriched objects (with distance_km +
    // suggested) — strip those before persisting.
    return (raw || []).map((a) => ({
      name: a?.name || '',
      lat: a?.lat ?? null,
      lng: a?.lng ?? null,
      radius_km: a?.radius_km ?? null,
    }));
  };

  useEffect(() => {
    if (!token) return;
    axios.get(`${API}/clock-in/areas`, { headers: { Authorization: `Bearer ${token}` } })
      .then((r) => {
        const list = normalize(r.data?.areas);
        setAreas(list);
        setOriginal(JSON.parse(JSON.stringify(list)));
      })
      .catch(() => setAreas([]))
      .finally(() => setLoading(false));
  }, [token]);

  const dirty = JSON.stringify(areas) !== JSON.stringify(original);

  const addArea = () => {
    const next = draft.trim().slice(0, 60);
    if (!next) return;
    if (areas.some((a) => a.name.toLowerCase() === next.toLowerCase())) {
      toast({ title: 'Already added', description: `"${next}" is already in your work areas.` });
      setDraft('');
      return;
    }
    setAreas((prev) => [...prev, { name: next, lat: null, lng: null, radius_km: null }]);
    setDraft('');
  };

  const removeArea = (idx) => {
    setAreas((prev) => prev.filter((_, i) => i !== idx));
    if (editingIdx === idx) setEditingIdx(-1);
  };

  const beginEdit = (idx) => {
    const a = areas[idx];
    setEditingIdx(idx);
    setEditLat(a.lat != null ? String(a.lat) : '');
    setEditLng(a.lng != null ? String(a.lng) : '');
    setEditRadius(a.radius_km != null ? String(a.radius_km) : '');
  };

  const cancelEdit = () => setEditingIdx(-1);

  const usePinnedGps = () => {
    if (!navigator.geolocation) {
      toast({ title: 'GPS unavailable', description: 'This browser does not expose location.', variant: 'destructive' });
      return;
    }
    setGpsBusy(true);
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        setEditLat(pos.coords.latitude.toFixed(6));
        setEditLng(pos.coords.longitude.toFixed(6));
        if (!editRadius) setEditRadius(String(DEFAULT_RADIUS_KM));
        setGpsBusy(false);
      },
      (err) => {
        setGpsBusy(false);
        toast({ title: 'Could not read location', description: err?.message || 'Permission denied or timed out.', variant: 'destructive' });
      },
      { timeout: 8000, enableHighAccuracy: true },
    );
  };

  const commitEdit = () => {
    const lat = editLat.trim() === '' ? null : Number(editLat);
    const lng = editLng.trim() === '' ? null : Number(editLng);
    const radius = editRadius.trim() === '' ? null : Number(editRadius);

    if (lat !== null && (Number.isNaN(lat) || lat < -90 || lat > 90)) {
      toast({ title: 'Invalid latitude', description: 'Must be a number between -90 and 90.', variant: 'destructive' });
      return;
    }
    if (lng !== null && (Number.isNaN(lng) || lng < -180 || lng > 180)) {
      toast({ title: 'Invalid longitude', description: 'Must be a number between -180 and 180.', variant: 'destructive' });
      return;
    }
    if (radius !== null && (Number.isNaN(radius) || radius <= 0)) {
      toast({ title: 'Invalid radius', description: 'Must be a positive number (km).', variant: 'destructive' });
      return;
    }

    setAreas((prev) => prev.map((a, i) => i === editingIdx ? {
      ...a,
      lat: lat,
      lng: lng,
      // Only persist a radius when coords are set; otherwise reset to null
      // so the backend uses the default 25km when the admin later pins.
      radius_km: (lat != null && lng != null) ? (radius ?? DEFAULT_RADIUS_KM) : null,
    } : a));
    setEditingIdx(-1);
  };

  const unpin = (idx) => {
    setAreas((prev) => prev.map((a, i) => i === idx ? { ...a, lat: null, lng: null, radius_km: null } : a));
    if (editingIdx === idx) setEditingIdx(-1);
  };

  const save = async () => {
    setSaving(true);
    try {
      const res = await axios.put(
        `${API}/clock-in/areas`,
        // Send only the fields the server cares about.
        { areas: areas.map((a) => a.lat != null && a.lng != null
            ? { name: a.name, lat: a.lat, lng: a.lng, radius_km: a.radius_km ?? DEFAULT_RADIUS_KM }
            : a.name) },
        { headers: { Authorization: `Bearer ${token}` } },
      );
      const list = normalize(res.data?.areas);
      setAreas(list);
      setOriginal(JSON.parse(JSON.stringify(list)));
      toast({ title: 'Work areas saved', description: 'Workers will see this list at their next clock-in.' });
    } catch (e) {
      toast({
        title: 'Save failed',
        description: e.response?.data?.detail || e.message || 'Try again.',
        variant: 'destructive',
      });
    } finally {
      setSaving(false);
    }
  };

  if (!isAdmin) {
    return (
      <Card className="bg-[#1A2433] border-[#2D3748]">
        <CardContent className="p-6 text-sm text-slate-400">
          Only administrators can configure work areas.
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-4" data-testid="work-areas-section">
      <Card className="bg-[#1A2433] border-[#2D3748]">
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-white">
            <MapPin className="w-5 h-5 text-emerald-400" />
            Work Areas
          </CardTitle>
          <p className="text-sm text-slate-400 mt-1">
            Define the locations or projects your workers can pick when they clock in
            (e.g. &ldquo;Calgary NE&rdquo;, &ldquo;Fort McMurray Site&rdquo;, &ldquo;Downtown Core&rdquo;).
            Pin an area to GPS coordinates and HinesTime will auto-suggest the closest one
            when a worker taps CLOCK IN. Leave empty to keep the legacy single-tap clock-in.
          </p>
        </CardHeader>
        <CardContent className="space-y-4">
          {loading ? (
            <div className="flex items-center gap-2 text-slate-400 text-sm">
              <Loader2 className="w-4 h-4 animate-spin" /> Loading…
            </div>
          ) : (
            <>
              {areas.length === 0 ? (
                <p className="text-sm text-slate-500 italic">
                  No work areas configured yet. Workers will see the regular single-tap clock-in.
                </p>
              ) : (
                <div className="space-y-2" data-testid="work-areas-list">
                  {areas.map((a, idx) => {
                    const pinned = a.lat != null && a.lng != null;
                    const slug = a.name.replace(/[^a-zA-Z0-9]/g, '-').toLowerCase();
                    return (
                      <div
                        key={`${a.name}-${idx}`}
                        className="border border-[#2D3748] rounded-lg p-3 bg-[#0F1923]"
                        data-testid={`work-area-row-${slug}`}
                      >
                        <div className="flex items-center justify-between gap-3 flex-wrap">
                          <div className="flex items-center gap-2 min-w-0">
                            <MapPin className={`w-4 h-4 flex-shrink-0 ${pinned ? 'text-emerald-400' : 'text-slate-500'}`} />
                            <span className="text-white font-medium truncate" title={a.name}>{a.name}</span>
                            {pinned && (
                              <Badge variant="outline" className="text-[10px] text-emerald-300 border-emerald-500/40 ml-1">
                                {a.lat.toFixed(3)}, {a.lng.toFixed(3)} · {a.radius_km ?? DEFAULT_RADIUS_KM}km
                              </Badge>
                            )}
                          </div>
                          <div className="flex items-center gap-1 flex-shrink-0">
                            <button
                              type="button"
                              onClick={() => beginEdit(idx)}
                              className="text-xs px-2 py-1 rounded text-blue-300 hover:bg-blue-500/10 border border-transparent hover:border-blue-500/40"
                              data-testid={`work-area-edit-${slug}`}
                            >
                              {pinned ? 'Edit pin' : 'Pin location'}
                            </button>
                            {pinned && (
                              <button
                                type="button"
                                onClick={() => unpin(idx)}
                                className="text-xs px-2 py-1 rounded text-amber-300 hover:bg-amber-500/10 border border-transparent hover:border-amber-500/40"
                                data-testid={`work-area-unpin-${slug}`}
                              >
                                Unpin
                              </button>
                            )}
                            <button
                              type="button"
                              onClick={() => removeArea(idx)}
                              className="text-xs px-2 py-1 rounded text-red-300 hover:bg-red-500/10 border border-transparent hover:border-red-500/40"
                              aria-label={`Remove ${a.name}`}
                              data-testid={`work-area-remove-${slug}`}
                            >
                              <Trash2 className="w-3 h-3" />
                            </button>
                          </div>
                        </div>

                        {/* Inline pin editor */}
                        {editingIdx === idx && (
                          <div className="mt-3 pt-3 border-t border-[#2D3748] space-y-2" data-testid={`work-area-editor-${slug}`}>
                            <div className="grid grid-cols-3 gap-2">
                              <Input
                                type="number"
                                step="0.000001"
                                value={editLat}
                                onChange={(e) => setEditLat(e.target.value)}
                                placeholder="Latitude"
                                className="bg-[#1A2433] border-[#2D3748] text-white text-sm h-9"
                                data-testid={`work-area-lat-${slug}`}
                              />
                              <Input
                                type="number"
                                step="0.000001"
                                value={editLng}
                                onChange={(e) => setEditLng(e.target.value)}
                                placeholder="Longitude"
                                className="bg-[#1A2433] border-[#2D3748] text-white text-sm h-9"
                                data-testid={`work-area-lng-${slug}`}
                              />
                              <Input
                                type="number"
                                step="0.1"
                                value={editRadius}
                                onChange={(e) => setEditRadius(e.target.value)}
                                placeholder={`Radius km (def ${DEFAULT_RADIUS_KM})`}
                                className="bg-[#1A2433] border-[#2D3748] text-white text-sm h-9"
                                data-testid={`work-area-radius-${slug}`}
                              />
                            </div>
                            <div className="flex flex-wrap gap-2 justify-between">
                              <Button
                                type="button"
                                size="sm"
                                variant="outline"
                                onClick={usePinnedGps}
                                disabled={gpsBusy}
                                className="text-xs border-emerald-500/50 text-emerald-300 hover:bg-emerald-500/10"
                                data-testid={`work-area-use-gps-${slug}`}
                              >
                                {gpsBusy ? <Loader2 className="w-3 h-3 mr-1 animate-spin" /> : <Crosshair className="w-3 h-3 mr-1" />}
                                Use my current GPS
                              </Button>
                              <div className="flex gap-2">
                                <Button
                                  type="button"
                                  size="sm"
                                  variant="outline"
                                  onClick={cancelEdit}
                                  className="text-xs"
                                  data-testid={`work-area-cancel-${slug}`}
                                >
                                  Cancel
                                </Button>
                                <Button
                                  type="button"
                                  size="sm"
                                  onClick={commitEdit}
                                  className="text-xs bg-blue-600 hover:bg-blue-700"
                                  data-testid={`work-area-confirm-${slug}`}
                                >
                                  Apply
                                </Button>
                              </div>
                            </div>
                            <p className="text-[10px] text-slate-500">
                              Leave fields blank and tap Apply to unpin. Default radius is 25km.
                            </p>
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}

              {/* Add input */}
              <div className="flex gap-2 pt-2 border-t border-[#2D3748]">
                <Input
                  value={draft}
                  onChange={(e) => setDraft(e.target.value)}
                  onKeyDown={(e) => e.key === 'Enter' && (e.preventDefault(), addArea())}
                  placeholder="e.g. Calgary NE"
                  maxLength={60}
                  className="bg-[#0F1923] border-[#2D3748] text-white"
                  data-testid="work-area-new-input"
                />
                <Button
                  onClick={addArea}
                  disabled={!draft.trim()}
                  className="bg-emerald-600 hover:bg-emerald-700"
                  data-testid="work-area-add-btn"
                >
                  <Plus className="w-4 h-4 mr-1" /> Add
                </Button>
              </div>

              {/* Save */}
              <div className="flex justify-end pt-2">
                <Button
                  onClick={save}
                  disabled={!dirty || saving}
                  className="bg-blue-600 hover:bg-blue-700"
                  data-testid="work-area-save-btn"
                >
                  {saving ? (
                    <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Saving…</>
                  ) : (
                    <><Save className="w-4 h-4 mr-2" /> Save changes</>
                  )}
                </Button>
              </div>
            </>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
